import { observer } from "mobx-react-lite";

import {
  CalendarEventStatus,
  CalendarEventStatusText
} from "@libs/gateways/booking/BookingGateway.dtos.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { withFetch } from "@ui-components/data-fetcher/DataFetcher.tsx";
import { FilterBar } from "@ui-components/filter-bar/FilterBar.tsx";
import { FilterBarProps } from "@ui-components/filter-bar/FilterBar.types.ts";
import { showUnlessPrintingStyles } from "@ui-components/printing/Print.styles.ts";

import { AppointmentInformationTable } from "./AppointmentInformationTable.tsx";

export type ApptinfoFilterType = {
  apptType?: string;
  apptDate?: Date;
  duration?: string;
  providerId?: string;
  status?: string[];
  claim?: string;
};

type AppointmentInformationModalInnerProps = {
  contactId: string;
  claimId: string | undefined;
  onDismissInfoModal: () => void;
};

const AppointmentInformationModalInnerBase: React.FC<AppointmentInformationModalInnerProps> =
  observer(({ contactId, claimId, onDismissInfoModal }) => {
    const { core, booking } = useStores();

    const appointmentStatusOptions =
      booking.ref.appointmentStatuses.keyTextValues;

    const statusOptions = appointmentStatusOptions.concat([
      {
        key: CalendarEventStatus.Cancelled,
        text: CalendarEventStatusText.CANCELLED
      }
    ]);

    const durationOptions = [
      { key: "0", text: "0 - 15 mins" },
      { key: "15", text: "15 - 30 mins" },
      { key: "30", text: "30 - 45 mins" },
      { key: "45", text: "45 - 60 mins" },
      { key: "60", text: "60+ mins" }
    ];

    const renderFilterItems: FilterBarProps["items"] = [
      {
        name: "apptType",
        type: "searchBox",
        stickItem: true,
        props: {
          autoFocus: true,
          placeholder: "Search appointments",
          styles: { root: { width: 200 } }
        }
      }
    ];

    if (!claimId && core.isNZTenant) {
      renderFilterItems.push({
        type: "searchBox",
        name: "claim",
        props: {
          placeholder: "Search claim no"
        }
      });
    }

    renderFilterItems.push(
      {
        type: "datePicker",
        name: "apptDate",
        props: {
          allowClear: true,
          placeholder: "Date"
        }
      },
      {
        type: "optionsSelect",
        name: "status",
        props: {
          placeholder: "Status",
          label: "Status",
          options: statusOptions,
          hideSearchOption: true,
          calloutWidth: 130,
          multiSelect: true
        }
      },
      {
        type: "optionsSelect",
        name: "duration",
        props: {
          placeholder: "Duration",
          label: "Duration",
          options: durationOptions,
          hideSearchOption: true,
          calloutWidth: 100
        }
      }
    );

    renderFilterItems.push({
      type: "bookableUsersSelect",
      name: "providerId",
      props: {
        multiSelect: false,
        usersFilter: { showOnCalendar: true },
        placeholder: "Providers",
        calloutWidth: 250
      }
    });

    return (
      <FilterBar<ApptinfoFilterType>
        items={renderFilterItems}
        styles={showUnlessPrintingStyles}
      >
        {() => (
          <AppointmentInformationTable
            contactId={contactId}
            claimId={claimId}
            onDismiss={onDismissInfoModal}
          />
        )}
      </FilterBar>
    );
  });

export const AppointmentInformationModalInner = withFetch(
  root =>
    root.core.isNZTenant
      ? root.practice.ref.accProviderContractTypes.load()
      : Promise.resolve(),
  AppointmentInformationModalInnerBase
);
